import React, { useContext, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import MetaTags from 'react-meta-tags';
import Catalog from '~/Catalog';
import EmailNotificationsBlock, {
  EmailNotificationsFormData,
} from '~/components/EmailNotificationsBlock';
import { ListItemForDeletion } from '~/components/Modals/components/ListItemCard';
import ContentContainerDefault from '~/components/ContentContainer/Default';
import withErrorBoundary from '~/ErrorBoundary';
import useCurrentUser from '~/hooks/useCurrentUser';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import useUsers from '~/hooks/useUsers';
import EmailSettingsSection from './components/SynchronisedMailboxesBlock';
import { emailNotificationsFormDataToMutation } from '~/components/EmailNotificationsBlock/util';
import {
  useGetUserQuery,
  UserRole,
  UserStatus,
  useUpdateMyDetailsMutation,
  useUpdateOfficeRelationMutation,
} from '~/graphql/types';
import AppErrorScreen from '~/components/AppErrorScreen';
import Loading from '~/components/Loading';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';
import ProfileHeader from './components/ProfileHeader';
import AccountContext from '~/contexts/AccountContext';
import FormContainer from './components/FormContainer';
import SignatureContainer from '~/components/SignatureContainer';
import DeleteUserFromOfficeModal from '~/components/Modals/DeleteUserFromOfficeModal';
import { asUserDetailsFormData } from './utils/asUserDetailsFormData';
import { convertOfficesList } from './utils/convertOfficesList';
import { createOfficeItemForModal } from './utils/createOfficeItemForModal';
import useIsMountedRef from '~/hooks/useIsMountedRef';
import { SectionContainer } from './Profile.style';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';

export type UserDetailsFormData = {
  name: string;
  email: string;
  phone: string;
  selectedMainOfficeId: string;
};

type Props = {
  hasError: boolean;
};

const text = {
  pageTitle: 'Profiel',
  imageUploadError:
    'Er is iets mis gegaan bij het uploaden van het bestand. Probeer het nog een keer.',
  phoneValidation: Catalog.invalidPhone,
  userUpdateError: Catalog.genericUnknownErrorMessage,
  title: 'Profiel',
  submitBtn: 'Wijzig wachtwoord',
  deleteLabel: 'Verwijder profiel',
  signature: 'Handtekening gebruiker',
};

const Profile: React.FC<Props> = ({ hasError }) => {
  const { refetchSessionHydration } = useContext(AccountContext);
  const [initialDataLoaded, setInitialDataLoaded] = useState<boolean>(false);
  const [cachedOfficeId, setCachedOfficeId] = useState<string | null>(null);
  const [cachedPhone, setCachedPhone] = useState<string | null>(null);
  const [showDeleteUserFromOfficeModal, setShowDeleteUserFromOfficeModal] =
    useState<Array<ListItemForDeletion> | null>(null);
  const addToast = useAddToast();

  const [updateMyDetailsMutation, { error: detailsMutationError }] =
    useUpdateMyDetailsMutation();
  const [updateOfficeRelationMutation, { error: officeMutationError }] =
    useUpdateOfficeRelationMutation();

  const me = useCurrentUser();

  const { id: accountId } = useCurrentAccount();

  const { data, networkStatus, refetch, startPolling, stopPolling } =
    useGetUserQuery({
      variables: {
        userId: me.id,
        accountId,
      },
    });

  const accountAdmins = useUsers().filter(
    user => user.role === UserRole.Admin && user.__typename !== 'DeletedUser',
  );

  const isLastAccountAdmin =
    accountAdmins.length === 1 && accountAdmins[0].id === me.id;

  const pollingTimeout: ReturnType<typeof setTimeout> | null = null;
  let isPolling: boolean = false;
  const isComponentMounted = useIsMountedRef();

  useEffect(
    () => () => {
      if (pollingTimeout) {
        clearTimeout(pollingTimeout);
      }
    },
    [],
  );

  const onSetInitialDataLoaded = () => {
    setInitialDataLoaded(true);
  };

  const onShowErrorMessage = (msg: string) =>
    addToast([formatToastMessage(msg, 'danger')]);

  const onSetCachedData = (data: {
    cachedPhone?: string | null;
    cachedOfficeId?: string | null;
  }) => {
    setCachedOfficeId(
      data.cachedOfficeId === undefined ? cachedOfficeId : data.cachedOfficeId,
    );
    setCachedPhone(
      data.cachedPhone === undefined ? cachedPhone : data.cachedPhone,
    );
  };

  const onUserUpdateError = () => {
    onShowErrorMessage(text.userUpdateError);
    onSetCachedData({
      cachedPhone: null,
    });
  };

  const handleDeleteUserFromOfficeModalAction = (
    officesList: Array<ListItemForDeletion> | null,
  ) => {
    setShowDeleteUserFromOfficeModal(officesList);
  };

  if (hasError) return <AppErrorScreen />;
  if (isInitialLoadStatus(networkStatus)) return <Loading />;
  if (!data || !data.getUser) return <AppErrorScreen />;

  const user = data.getUser;

  const mailboxes = user.Mailboxes;
  const signature = user.Signature ?? null;

  const refetchUser = () => processRefetch();

  const onRelationMutationError = () => {
    void processRefetch();
    onSetCachedData({ cachedOfficeId: null });
    onShowErrorMessage(text.userUpdateError);
  };

  const processRefetch = async () => {
    if (!initialDataLoaded) onSetInitialDataLoaded();

    const { data } = await refetch();
    const { getUser } = data;
    if (getUser === null) {
      return;
    }

    if (cachedPhone && cachedPhone === getUser?.phone) {
      onSetCachedData({
        cachedPhone: null,
      });
    }

    const updatedCachedOffice = getUser?.Offices.find(
      item => item.id === cachedOfficeId,
    );

    if (
      updatedCachedOffice != null &&
      updatedCachedOffice.relation != null &&
      updatedCachedOffice.relation.mainOffice === true
    ) {
      onSetCachedData({
        cachedOfficeId: null,
      });
    }

    if (!isComponentMounted) return;

    await refetchSessionHydration();
  };

  if (detailsMutationError) onUserUpdateError();

  const updateMyName = (name: string) =>
    updateMyDetailsMutation({
      variables: {
        accountId,
        update: {
          name,
        },
      },
    });

  const updateUserDetails = (newFormData: UserDetailsFormData) => {
    const { phone, name } = newFormData;

    onSetCachedData({
      cachedPhone: phone,
    });

    return updateMyDetailsMutation({
      variables: {
        accountId: accountId,
        update: {
          name,
          phone,
        },
      },
    });
  };

  const updateEmailNotifications = (
    newFormData: EmailNotificationsFormData,
  ) => {
    const vars = emailNotificationsFormDataToMutation(newFormData);

    return updateMyDetailsMutation({
      variables: {
        accountId,
        update: vars,
      },
    });
  };

  const userIsBeingDeletedFromOffice = user.Offices.some(
    office => office.relation?.status === UserStatus.Deleting,
  );

  if (userIsBeingDeletedFromOffice) {
    if (isPolling === false) {
      startPolling(3000);
      isPolling = true;
    }
  }

  if (!userIsBeingDeletedFromOffice) {
    if (isPolling === true) {
      stopPolling();
      isPolling = false;
    }
  }

  if (officeMutationError) onRelationMutationError();

  const updateMainOffice = (officeId: string): Promise<any> => {
    const update = { mainOffice: true };

    onSetCachedData({
      cachedOfficeId: officeId,
    });

    return updateOfficeRelationMutation({
      variables: {
        accountId,
        officeId,
        userId: me.id,
        update,
      },
    });
  };

  const userFormData = {
    email: user ? user.email.toLowerCase() : '',
    phone: cachedPhone || user ? user.phone : '',
    cachedOfficeId: cachedOfficeId,
  };

  const initialUserDetailValues = asUserDetailsFormData(user);

  const onUserDetailsFormSubmit = (formNewData: UserDetailsFormData) => {
    void updateUserDetails(formNewData);

    // check is office role realy changed and call mutation if true
    const selectedOfficeId = formNewData.selectedMainOfficeId;
    const officeInitial = user.Offices.find(
      item => item.id === selectedOfficeId,
    );

    if (officeInitial) {
      if (
        officeInitial.relation &&
        officeInitial.relation.mainOffice !== true
      ) {
        return updateMainOffice(selectedOfficeId); // id of changed office
      }
    }
    return;
  };

  const onEmailNotificationsFormSubmit = (
    formNewData: EmailNotificationsFormData,
  ) => updateEmailNotifications(formNewData);

  return (
    <ContentContainerDefault>
      <MetaTags>
        <title>{text.pageTitle}</title>
      </MetaTags>
      <section data-testid="user-profile-page">
        <ProfileHeader
          user={user}
          me={me}
          refetchUser={refetchUser}
          isLastAccountAdmin={isLastAccountAdmin}
          updateMyName={updateMyName}
        />
        <SectionContainer>
          <LeftContainer>
            <FormContainer
              userFormData={userFormData}
              officesList={convertOfficesList(user)}
              onUserDetailsFormSubmit={onUserDetailsFormSubmit}
              initialUserDetailsValues={initialUserDetailValues}
              showDeleteModal={officeId =>
                handleDeleteUserFromOfficeModalAction([
                  createOfficeItemForModal(user.Offices, officeId),
                ])
              }
              mayEdit={user.id === me.id}
              dataIsPolling={isPolling}
              refetch={refetch}
              signature={signature}
              userId={me.id}
            />

            <SignatureContainer
              title={text.signature}
              signature={signature}
              refetch={refetch}
              entityTypeForMutation={{ userId: me.id }}
            />
          </LeftContainer>

          <div>
            <EmailSettingsSection
              mailboxes={mailboxes}
              startPolling={startPolling}
              stopPolling={stopPolling}
              refetch={refetch}
            />

            <EmailNotificationsBlock
              onFormSubmit={onEmailNotificationsFormSubmit}
              notificationSettings={user.NotificationSettings}
            />
          </div>

          {showDeleteUserFromOfficeModal && (
            <DeleteUserFromOfficeModal
              list={showDeleteUserFromOfficeModal}
              onCancel={() => handleDeleteUserFromOfficeModalAction(null)}
              onClose={() => handleDeleteUserFromOfficeModalAction(null)}
              refetch={refetchUser}
              userId={me.id}
            />
          )}
        </SectionContainer>
      </section>
    </ContentContainerDefault>
  );
};

const LeftContainer = styled.div<{}>`
  ${({ theme }) => css`
    margin-right: ${theme.space('m')};

    ${theme.mq.greaterThan('tablet')`
      margin: 0;
    `}
  `}
`;

export default withErrorBoundary(Profile);
